import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import {
  cqAccessPointsQuery,
  saveCqAccessPoint,
  type CqAccessPoint,
  type CqAccessPointsPage,
} from '../../api/connectionQuality'
import { toast } from '../../ui/notify'
import '../../devices/CqWifi.css'
import '../download_agent/DownloadAgentPage.css'
import '../connection_quality/ConnectionQualitySettingsPage.css'

/**
 * Wi-Fi access points - lista AP-urilor firmei (Connection Quality faza 3),
 * in coloana din dreapta a paginii Settings -> Connection Quality, sub cardul
 * Floor plans.
 *
 * Un rand = un AP fizic, cum il grupeaza serverul (BssidGroups.php): BSSID-urile
 * lui, clientii din ultimele 24 de ore, semnalul si retransmisiile mediane,
 * marcajele "Weak AP" si "Sticky clients". Numele si sediul se scriu cu
 * dreptul de editare al firmei; grupurile cu acelasi nume se arata ca unul
 * singur - asa corecteaza omul o grupare gresita.
 *
 * Fara drept de citire panoul nu apare. Fara niciun AP inca, apare cu
 * explicatia de ce e gol - un card lipsa nu-i spune omului ca lista exista.
 */
export function AccessPointsPanel() {
  const { data, error } = useQuery(cqAccessPointsQuery)
  if (error || !data) return null
  if (!Array.isArray(data.aps) || data.aps.length === 0) return <Empty />
  return <Panel page={data} />
}

function Panel({ page }: { page: CqAccessPointsPage }) {
  const [editing, setEditing] = useState<string | null>(null)
  const office = (id: number | null) => page.offices.find((o) => o.id === id)?.name ?? null

  return (
    <section data-panel="access-points">
      <Heading>The access points your devices connected to in the last {page.hours} hours, one row per physical access point.</Heading>
      <div className="od-da-card od-cq-aps">
        <div className="table-responsive">
          <table className="table table-hover cq-aps-table">
            <thead>
              <tr>
                <th>Access point</th>
                <th>Office</th>
                <th className="num">Clients ({page.hours}h)</th>
                <th className="num">Median signal</th>
                <th className="num">Median retries</th>
                <th>Marks</th>
              </tr>
            </thead>
            <tbody>
              {page.aps.map((ap) =>
                editing === ap.key ? (
                  <EditRow key={ap.key} ap={ap} page={page} onDone={() => setEditing(null)} />
                ) : (
                  <tr key={ap.key} data-ap={ap.key}>
                    <td>
                      <strong>{ap.name ?? ap.key}</strong>
                      {page.canWrite ? (
                        <button type="button" className="btn btn-link btn-xs" onClick={() => setEditing(ap.key)} aria-label={`Rename ${ap.name ?? ap.key}`}>
                          <i className="fas fa-pen" aria-hidden="true"></i> Edit
                        </button>
                      ) : null}
                      <div className="cq-aps-bssids">
                        {ap.bssids.map((b) => (
                          <span key={b.bssid} className={b.seen ? '' : 'is-unseen'} title={b.seen ? 'seen in the last 24 hours' : 'not seen in the last 24 hours'}>
                            {b.bssid}{' '}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>{office(ap.office_id) ?? <span className="cq-muted">-</span>}</td>
                    <td className="num" title={ap.devices.map((d) => d.name || `Device ${d.id}`).join(', ')}>
                      {ap.clients}
                    </td>
                    <td className="num">{ap.median_rssi === null ? '-' : `${ap.median_rssi} dBm`}</td>
                    <td className="num">{ap.median_retry_pct === null ? '-' : `${ap.median_retry_pct}%`}</td>
                    <td>
                      {ap.weak ? (
                        <span className="cq-aps-mark is-weak" data-mark="weak">
                          Weak AP
                        </span>
                      ) : null}
                      {ap.sticky.length ? (
                        <span
                          className="cq-aps-mark is-sticky"
                          data-mark="sticky"
                          title={ap.sticky.map((c) => `${c.name || `Device ${c.id}`} (${c.rssi ?? '-'} dBm)`).join(', ')}
                        >
                          Sticky clients: {ap.sticky.length}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
        <p className="pl-2 pr-2 cq-aps-help">
          Weak AP: the median signal of its clients is below {page.thresholds.weak_rssi} dBm, or their median retries are above{' '}
          {page.thresholds.weak_retry_pct}%. Sticky clients: devices that stay on it for at least {page.thresholds.sticky_minutes}{' '}
          minutes with a signal below {page.thresholds.sticky_rssi} dBm. BSSIDs that share the first five bytes are grouped as one
          physical access point; if two access points were merged or one was split, give them names - access points with the same
          name are shown as one.
        </p>
      </div>
    </section>
  )
}

function Empty() {
  return (
    <section data-panel="access-points">
      <Heading>The access points your devices connect to, one row per physical access point.</Heading>
      <div className="od-da-card od-cq-aps">
        <div className="od-cq-empty" data-state="empty">
          <strong>No access points yet.</strong>
          They appear here on their own once devices on Wi-Fi report their measurements. Turn on Measure the connection (step 1)
          and, for Windows 11 machines, Wi-Fi diagnostics (step 2) - without it Windows hides which access point a machine uses.
        </div>
      </div>
    </section>
  )
}

/** Titlul de sectiune, in afara cardului - la fel ca "Default Connection Quality" din stanga. */
function Heading({ children }: { children: ReactNode }) {
  return (
    <div className="od-cq-section">
      <h3>Wi-Fi access points</h3>
      <p>{children}</p>
    </div>
  )
}

function EditRow({ ap, page, onDone }: { ap: CqAccessPoint; page: CqAccessPointsPage; onDone(): void }) {
  const query = useQueryClient()
  const [name, setName] = useState(ap.name ?? '')
  const [officeId, setOfficeId] = useState(ap.office_id === null ? '' : String(ap.office_id))
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState('')

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setProblem('')
    saveCqAccessPoint({ keys: ap.keys, name: name.trim(), office_id: officeId === '' ? null : Number(officeId) })
      .then(async (next) => {
        query.setQueryData(cqAccessPointsQuery.queryKey, next)
        await query.invalidateQueries({ queryKey: ['device'] })
        toast('Saved.', 'Wi-Fi access points')
        onDone()
      })
      .catch((e: unknown) => {
        const message = e instanceof ApiError && e.message ? e.message : 'It could not be saved.'
        setProblem(message)
        setBusy(false)
      })
  }

  return (
    <tr data-ap={ap.key} className="is-editing">
      <td colSpan={6}>
        <form className="cq-aps-edit" onSubmit={submit}>
          <input
            type="text"
            className="form-control input-sm"
            aria-label="Access point name"
            placeholder={ap.key}
            maxLength={128}
            value={name}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
          />
          <select className="form-control input-sm" aria-label="Office" value={officeId} disabled={busy} onChange={(e) => setOfficeId(e.target.value)}>
            <option value="">No office</option>
            {page.offices.map((o) => (
              <option key={o.id} value={String(o.id)}>
                {o.name}
              </option>
            ))}
          </select>
          <button type="submit" className="btn btn-success btn-sm" disabled={busy}>
            Save
          </button>
          <button type="button" className="btn btn-default btn-sm" disabled={busy} onClick={onDone}>
            Cancel
          </button>
          <span className="cq-aps-help">{ap.bssids.map((b) => b.bssid).join(' ')}</span>
          {problem ? (
            <span className="text-danger" role="alert">
              {problem}
            </span>
          ) : null}
        </form>
      </td>
    </tr>
  )
}
